import { 
    Box, 
    Checkbox, 
    FormControl, 
    FormControlLabel, 
    Grid, 
    InputLabel, 
    MenuItem, 
    OutlinedInput, 
    Select, 
    TextField, 
    Tooltip,
    Typography 
} from "@mui/material";
import { IOSSwitch } from '../../../Components/UI/Switch/IOSSwitch';
import { useEffect, useMemo, useState } from "react";
import { formatDecimal } from '../../../Utils/numbers.js'
import { CATEGORIAS, PROVINCIAS } from "../constants.js";
import { PopoverComponent } from '../../../Components/Notifications/PopoverComponent';

export const Isib = ({record, afip, setAfip}) => {
    const isib = record?.afip?.isib;

    const [enabled, setEnabled] = useState(isib?.enabled ?? false);
    const [categoria, setCategoria] = useState(isib?.categoria ?? "");
    const [convenio, setConvenio] = useState(!!isib?.convenio_multilateral);
    const [promocion, setPromocion] = useState(!!isib?.promocion_economica);
    const [regGeneral, setRegGeneral] = useState(isib?.categoria === 'gravado' && !isib?.convenio_multilateral);
    const provincia = afip?.domicilio?.provincia ?? record?.afip?.provincia;

    const [alicuotaInput, setAlicuotaInput] = useState(() => formatDecimal(isib?.alicuota));

    const alicuotaNum = useMemo(() => {
        const parsed = parseFloat(alicuotaInput.replace(",", "."));
        return isNaN(parsed) ? 0 : Math.min(Math.max(parsed, 0), 100);
    }, [alicuotaInput]);

    const isGravado = categoria === "gravado";

    const categoriaLabel = useMemo(() => CATEGORIAS.find(c => c.id === categoria)?.name ?? "", [categoria]);

    const updateAfipIsib = (updatedFields) => {
        setAfip((prevAfip) => ({
            ...prevAfip,
            isib: {
                ...prevAfip?.isib,
                ...updatedFields,
            },
        }));
    };    

    const handleEnabledChange = (e) => {
        const isChecked = e.target.checked;
        setEnabled(isChecked);
        updateAfipIsib({ enabled: isChecked });
    };

    const handleCategoriaChange = (e) => {
        const val = e.target.value;
        setCategoria(val);
        updateAfipIsib({ categoria: val })
    };

    const handleConvenioChange = (e) => {
        const val = e.target.checked;
        if (regGeneral) setRegGeneral(!val);
        setConvenio(val);
        updateAfipIsib({ convenio_multilateral: val })
    };

    const handlePromocionChange = (e) => {
        const val = e.target.checked;
        setPromocion(val);
        updateAfipIsib({ promocion_economica: val });
    };

    const handleRegimenGeneralChange = (e) => {
        const val = e.target.checked;
        setRegGeneral(val);
        if (val) {
            setConvenio(false);
            updateAfipIsib({ convenio_multilateral: false });
        }
    };

    const handleAlicuotaChange = (e) => {
        let val = e.target.value.replace(/[^0-9,]/g, "");
        const parts = val.split(",");
        if (parts.length > 2) val = `${parts[0]},${parts.slice(1).join("")}`;
        if (parts[0] && parseInt(parts[0], 10) > 100) return;
        if (parts[1] && parts[1].length > 2) val = `${parts[0]},${parts[1].slice(0, 2)}`;

        setAlicuotaInput(val);
        const parsed = parseFloat(val.replace(",", "."));
        updateAfipIsib({ alicuota: isNaN(parsed) ? 0 : parsed })
    };   
  
    const handleAlicuotaBlur = () => {
        setAlicuotaInput(formatDecimal(alicuotaNum));
        updateAfipIsib({ alicuota: alicuotaNum })
    };  

    useEffect(() => {
        if (!isGravado) {
            setAlicuotaInput(formatDecimal(0))
            setConvenio(false)
            setRegGeneral(false)
            setPromocion(false)

            updateAfipIsib({ 
                alicuota: 0,
                convenio_multilateral: false,
                promocion_economica: false
            })
        }
    }, [categoria, isGravado]);

    const getProvincia = (prov) => {
        if(!prov) return ''
        const provUpper = String(prov).toUpperCase();
        return PROVINCIAS[provUpper] ?? provUpper;
    }

    const leyenda = useMemo(() => {
        const provMapped = getProvincia(provincia)
        const fraseFinal = provMapped ? `ATRIBUIDOS A ${provMapped}` : '';
        if (categoria === "exento") return `OPERACIÓN EXENTA ISIB ${provMapped}`;
        if (isGravado) {
            const lines = [`ALICUOTA ISIB ${provMapped} ${formatDecimal(alicuotaNum)}%`];
            if (convenio) lines.push(`APLICABLE SOBRE INGRESOS BRUTOS ${fraseFinal}`);
            if (promocion) lines.push(`OPERACIÓN ALCANZADA POR REGÍMENES DE PROMOCIÓN ECONÓMICA ${provMapped}`);
            return lines.join("\n");
        }
        return "(SIN LEYENDA)";
    }, [categoria, isGravado, alicuotaNum, convenio, promocion, provincia]);
  
    return (
        <Grid container columns={{xs: 4, sm: 8, md: 12}} spacing={2}>
            <Grid item xs={12}>
                <Box
                    sx={{
                        width: '100%',
                        display: 'flex',
                        flexDirection: 'row',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '10px',
                        paddingBottom: '5px',
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '20px',
                        }}
                    >
                    <Typography
                        sx={{
                            textTransform: 'uppercase',
                            fontSize: '.8em',
                            fontWeight: '600',
                            color: '#000000',
                        }}
                    >
                        Leyenda de impuesto sobre ingresos brutos (ISIB)
                    </Typography>
                    <PopoverComponent text={'Al activar esta sección, las facturas emitidas por este negocio incluirán leyendas ISIB como se muestran en la previsualización.'} />
                    </Box>
                    <IOSSwitch 
                        sx={{
                            width: 40, 
                            height: 25,
                            alignItems: 'center',
                            '& .MuiSwitch-thumb': {
                            boxSizing: 'border-box',
                            width: 20,
                            height: 20,
                            },
                            '& .MuiSwitch-track': {
                            height: 25,
                            },
                        }} 
                        checked={enabled} 
                        onChange={handleEnabledChange} 
                    />
                </Box>
            </Grid>
            <Grid item xs={12} md={6}>
                <Tooltip title={categoriaLabel} arrow disableHoverListener={!categoria}>
                    <FormControl 
                        variant="outlined"
                        fullWidth
                    >
                        <InputLabel id="isib-select-label">Categoría *</InputLabel>
                        <Select
                            labelId="isib-select-label"
                            id="isib-select"
                            label="Categoría *"
                            value={categoria}
                            onChange={handleCategoriaChange}
                            MenuProps={{
                                PaperProps: {
                                    sx: {
                                        maxWidth: { xs: '90vw', md: 380 }
                                    }
                                }
                            }}
                            sx={{
                                '& .MuiSelect-select': {
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap'
                                }
                            }}
                        >
                            {CATEGORIAS.map((item) => (
                                <MenuItem 
                                    key={item.id} 
                                    value={item.id}
                                    sx={{
                                        whiteSpace: 'normal',
                                        wordWrap: 'break-word',
                                        wordBreak: 'break-word',
                                    }}
                                >
                                    {item.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Tooltip>
            </Grid>
            <Grid item xs={12} md={6}>
                <TextField
                    id="isib-alicuota"
                    label="Alícuota"
                    InputLabelProps={{ shrink: true }}
                    variant="outlined"
                    disabled={!isGravado}
                    value={alicuotaInput}
                    //fullWidth
                    width='auto'
                    onChange={handleAlicuotaChange}
                    onBlur={handleAlicuotaBlur}
                    inputProps={{ inputMode: 'decimal' }}
                />
                <PopoverComponent 
                    sx={{}}
                    text={'Cuando en la factura o documento equivalente se reflejen diversas operaciones alcanzadas por múltiples alícuotas, solo debe consignarse la alícuota menor.'} 
                />
            </Grid>
            <Grid item xs={12}>
                <Typography
                    sx={{
                        color: '#1f2124',
                        fontSize: '18px',
                        fontWeight: 350,
                        letterSpacing: 0.2,
                        display: 'inline-block',
                        width: 'fit-content',
                        borderBottom: '1px solid #cfd8dc',
                        pb: 0.5
                    }}
                >Tipo de régimen
                </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
                <FormControlLabel 
                    control={<Checkbox 
                        sx={{ color: "#5584AC" }} 
                        checked={convenio}
                        onChange={handleConvenioChange}
                        disabled={!isGravado}
                    />} 
                    label='Convenio multilateral' 
                />
            </Grid>
            <Grid item xs={12} md={6}>
                <FormControlLabel 
                    control={<Checkbox 
                        sx={{ color: "#5584AC" }} 
                        checked={promocion}
                        onChange={handlePromocionChange}
                        disabled={!isGravado}
                    />} 
                    label='Promoción económica' 
                />
            </Grid>
            <Grid item xs={12} md={6}>
                <FormControlLabel 
                    control={<Checkbox 
                        sx={{ color: "#5584AC" }} 
                        checked={regGeneral}
                        onChange={handleRegimenGeneralChange}
                        disabled={!isGravado}
                    />} 
                    label='Régimen general' 
                />
            </Grid>
            <Grid item xs={12}>
                <Typography
                    sx={{
                        color: '#1f2124',
                        fontSize: '18px',
                        fontWeight: 350,
                        letterSpacing: 0.2,
                        display: 'inline-block',
                        width: 'fit-content',
                        borderBottom: '1px solid #cfd8dc',
                        pb: 0.5
                    }}
                >Previsualización de leyenda ISIB
                </Typography>
            </Grid>            
            <Grid item xs={12}>
                <Box sx={{
                    backgroundColor: '#eeeeee', 
                    borderRadius: '3px', 
                    marginBottom: '10px', 
                    width: '100%', 
                    padding: '15px', 
                    display: 'flex', 
                    flexDirection: 'row', 
                    justifyContent: 'space-between', 
                    alignItems: 'center'
                }}>
                    <Typography style={{marginLeft: '10px', fontSize: '17px', fontWeight: '400', width: '100%', whiteSpace: 'pre-line'}}>
                        {leyenda}
                    </Typography>
                </Box>
            </Grid>
        </Grid>
    );
}